import React, { useEffect } from 'react';
import { Form, Input, Select, Checkbox } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { useCustomFieldDefinitions } from 'src/hooks/useCustomFields';
import {
  LabeledCustomNumberInput,
  numberFieldError,
} from 'src/components/CustomNumberInput';

type Props = {
  values: Record<string, any>;
  onChange: (customFields: Record<string, any>) => void;
  disabled?: boolean;
  /** Called when number-field validity changes (true = all valid). */
  onValidityChange?: (valid: boolean) => void;
};

/**
 * Controlled editor for project-level custom fields (no react-hook-form).
 * Values are stored under project.data.custom_fields.
 */
export function ProjectCustomFields({
  values = {},
  onChange,
  disabled = false,
  onValidityChange,
}: Props) {
  const projectId = useSelector((state: any) => state.mainFrame.projectId);
  const { data: allDefs } = useCustomFieldDefinitions(projectId);
  const fields = allDefs?.project ?? [];

  const hasErrors = fields.some(
    (field: any) =>
      field.kind === 'number' &&
      numberFieldError(
        values[field.id] !== undefined ? values[field.id] : field.default,
        field
      )
  );

  useEffect(() => {
    onValidityChange?.(!hasErrors);
  }, [hasErrors, onValidityChange]);

  if (fields.length === 0) return null;

  const handleChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <>
      {fields.map((field: any) => {
        const value =
          values[field.id] !== undefined ? values[field.id] : field.default;
        const numberError =
          field.kind === 'number' ? numberFieldError(value, field) : null;

        if (field.kind === 'number') {
          return (
            <LabeledCustomNumberInput
              key={field.id}
              label={field.name}
              value={value}
              precision={field.precision ?? 0}
              min={field.min}
              max={field.max}
              disabled={disabled}
              error={numberError}
              onChange={(next) => handleChange(field.id, next)}
            />
          );
        }

        return (
          <Form.Field key={field.id}>
            <label>{field.name}</label>
            {field.kind === 'text' && (
              <Input
                value={value ?? ''}
                disabled={disabled}
                onChange={(e, { value: v }) => handleChange(field.id, v)}
              />
            )}
            {field.kind === 'choice' && (
              <Select
                options={(field.choices ?? []).map((c: string) => ({
                  key: c,
                  value: c,
                  text: c,
                }))}
                value={value ?? ''}
                disabled={disabled}
                onChange={(_, d) => handleChange(field.id, d.value)}
              />
            )}
            {field.kind === 'list' && (
              <Select
                multiple
                options={(field.choices ?? []).map((c: string) => ({
                  key: c,
                  value: c,
                  text: c,
                }))}
                value={value ?? []}
                disabled={disabled}
                onChange={(_, d) => handleChange(field.id, d.value)}
              />
            )}
            {field.kind === 'boolean' && (
              <Checkbox
                checked={!!value}
                disabled={disabled}
                onChange={(_, d) => handleChange(field.id, !!d.checked)}
              />
            )}
          </Form.Field>
        );
      })}
    </>
  );
}

export default ProjectCustomFields;
